<template>
    <UiModal
        v-if="!isMobile"
        :isOpen="isOpen"
        @close="handleClose"
        width="600px"
        :fullScreenMobile="true"
    >
        <template #title>
            <div class="flex-1 min-w-0 pr-2">
                <h5 class="text-slate-800 dark:text-slate-100 text-xl font-medium leading-7">
                    {{
                        mode === 'add'
                            ? 'Add redemption to trip'
                            : 'Move redemption to trip'
                    }}
                </h5>
                <p
                    v-if="subtitle"
                    class="text-[#64748B] dark:text-slate-400 text-sm leading-5 font-normal mt-1 break-words"
                >
                    {{ subtitle }}
                </p>
            </div>
        </template>

        <template #content>
            <!-- Missing details warning -->
            <div
                v-if="hasMissingDetails"
                class="mb-4 w-full p-4 rounded-lg bg-[#F1F5F9] dark:bg-slate-700"
            >
                <div class="flex items-start gap-3 flex-1">
                    <div
                        class="flex items-center justify-center w-6 h-6 shrink-0"
                    >
                        <Icon
                            icon="circle-info"
                            class="text-slate-800 dark:text-slate-100"
                            :width="20"
                            :height="20"
                        />
                    </div>
                    <div class="flex flex-col">
                        <p class="text-sm font-medium leading-5 text-[#1E293B] dark:text-slate-100">
                            Missing required details:
                        </p>
                        <p class="text-sm font-normal leading-5 text-[#64748B] dark:text-slate-400">
                            Review and complete details before adding to a trip.
                        </p>
                    </div>
                </div>
            </div>

            <!-- Loading -->
            <div
                v-if="loadingTrips"
                class="flex items-center justify-center py-8"
            >
                <i
                    class="fas fa-spinner-third fa-spin text-slate-400 text-lg"
                ></i>
            </div>

            <template v-else>
                <p class="text-[#475569] dark:text-slate-400 text-sm font-medium leading-5 mb-2">
                    {{
                        mode === 'add'
                            ? 'Select trip to add redemption'
                            : 'Select trip to move redemption'
                    }}
                </p>

                <!-- Trip list -->
                <div class="max-h-[50vh] overflow-y-auto -mx-2 px-2">
                    <template
                        v-for="(trip, index) in filteredTrips"
                        :key="trip.id"
                    >
                        <div
                            class="flex items-center gap-4 py-4 cursor-pointer transition-all rounded-lg px-2 -mx-2 focus:outline-none focus:ring-2 focus:bg-slate-50 dark:bg-slate-900/40 focus:ring-inset focus:ring-slate-300"
                            :class="
                                selectedTripId === trip.id
                                    ? 'bg-slate-50 dark:bg-slate-900/40'
                                    : 'hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900/40'
                            "
                            tabindex="0"
                            role="option"
                            :aria-selected="selectedTripId === trip.id"
                            @click="
                                selectedTripId = trip.id;
                                validationError = false;
                            "
                            @keyup.enter="
                                selectedTripId = trip.id;
                                validationError = false;
                            "
                            @keyup.space="
                                selectedTripId = trip.id;
                                validationError = false;
                            "
                        >
                            <div
                                class="w-12 h-12 rounded-lg shrink-0 bg-slate-100 dark:bg-slate-700 bg-center bg-cover trip-thumbnail"
                                :style="
                                    getTripImageUrl(trip)
                                        ? {
                                              backgroundImage: `url(${getTripImageUrl(trip)})`,
                                          }
                                        : {}
                                "
                            ></div>
                            <div class="min-w-0 flex-1">
                                <p
                                    class="text-sm font-medium text-[#1E293B] dark:text-slate-100 leading-5 truncate"
                                >
                                    {{ trip.title }}
                                </p>
                                <p class="text-sm text-[#64748B] dark:text-slate-400 leading-5">
                                    {{ formatTripDate(trip.start_date) }}
                                    <span v-if="trip.end_date">
                                        – {{ formatTripDate(trip.end_date) }}
                                    </span>
                                </p>
                            </div>
                            <div
                                v-if="selectedTripId === trip.id"
                                class="ml-auto flex items-center justify-center w-8 h-8 rounded-full bg-[#FD421D] text-white shrink-0"
                            >
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M13.3334 4L6.00008 11.3333L2.66675 8"
                                        stroke="white"
                                        stroke-width="2.5"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                </svg>
                            </div>
                        </div>
                        <hr
                            v-if="index < filteredTrips.length - 1"
                            class="border-t border-[#E2E8F0] dark:border-slate-700"
                        />
                    </template>

                    <!-- New trip option -->
                    <hr
                        v-if="filteredTrips.length > 0"
                        class="border-t border-[#E2E8F0] dark:border-slate-700"
                    />
                    <div
                        class="flex items-center gap-4 py-4 cursor-pointer transition-all rounded-lg px-2 -mx-2 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-slate-300"
                        :class="
                            selectedTripId === 'new'
                                ? 'bg-slate-50 dark:bg-slate-900/40'
                                : 'hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900/40'
                        "
                        tabindex="0"
                        role="option"
                        :aria-selected="selectedTripId === 'new'"
                        @click="
                            selectedTripId = 'new';
                            validationError = false;
                        "
                        @keyup.enter="
                            selectedTripId = 'new';
                            validationError = false;
                        "
                        @keyup.space="
                            selectedTripId = 'new';
                            validationError = false;
                        "
                    >
                        <div
                            class="w-12 h-12 rounded-lg shrink-0 bg-slate-100 dark:bg-slate-700 flex items-center justify-center"
                        >
                            <Icon
                                icon="trip-insurance"
                                :width="20"
                                :height="20"
                                class="text-slate-500 dark:text-slate-400"
                                pathClass="fill-slate-500"
                            />
                        </div>
                        <div class="min-w-0 flex-1">
                            <p
                                class="text-sm font-medium text-[#1E293B] dark:text-slate-100 leading-5"
                            >
                                New trip
                            </p>
                            <p class="text-sm text-[#64748B] dark:text-slate-400 leading-5">
                                {{
                                    filteredTrips.length === 0
                                        ? 'You can specify trip details in next step'
                                        : 'Create a new trip and add redemption'
                                }}
                            </p>
                        </div>
                        <div
                            v-if="selectedTripId === 'new'"
                            class="ml-auto flex items-center justify-center w-8 h-8 rounded-full bg-10x-orange text-white shrink-0"
                        >
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M13.3334 4L6.00008 11.3333L2.66675 8"
                                    stroke="white"
                                    stroke-width="2.5"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                        </div>
                    </div>
                </div>

                <!-- Validation error -->
                <div
                    v-if="validationError"
                    class="flex items-center gap-1.5 py-2"
                >
                    <Icon
                        icon="caution"
                        :width="14"
                        :height="14"
                        class="text-red-500"
                    />
                    <span class="text-sm text-red-500">
                        {{
                            mode === 'add'
                                ? 'Select trip to add redemption'
                                : 'Select trip to move redemption'
                        }}
                    </span>
                </div>
            </template>
        </template>

        <template #footer>
            <div class="flex justify-end items-center gap-3">
                <button
                    type="button"
                    class="px-4 py-2 text-sm font-medium text-slate-800 dark:text-slate-100 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900/40"
                    @click="handleClose"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    class="px-4 py-2 text-sm font-medium text-white bg-slate-800 rounded shadow-sm hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    :disabled="submitting || hasMissingDetails"
                    @click="handleConfirm"
                >
                    {{
                        selectedTripId === 'new'
                            ? 'Create new trip'
                            : mode === 'add'
                              ? 'Save & add to trip'
                              : 'Move redemption'
                    }}
                    <span class="inline ml-2" v-if="submitting">
                        <i class="fas fa-spinner-third fa-spin"></i>
                    </span>
                </button>
            </div>
        </template>
    </UiModal>

    <UiModalMobile
        v-else
        :isOpen="isOpen"
        @close="handleClose"
        :title="
            mode === 'add'
                ? 'Add redemption to trip'
                : 'Move redemption to trip'
        "
    >
        <template #content>
            <p
                v-if="subtitle"
                class="text-[#64748B] dark:text-slate-400 text-sm leading-5 font-normal mb-3 break-words"
            >
                {{ subtitle }}
            </p>

            <!-- Missing details warning -->
            <div
                v-if="hasMissingDetails"
                class="mb-4 w-full p-4 rounded-lg bg-[#F1F5F9] dark:bg-slate-700"
            >
                <div class="flex items-start gap-3 flex-1">
                    <div
                        class="flex items-center justify-center w-6 h-6 shrink-0"
                    >
                        <Icon
                            icon="circle-info"
                            class="text-slate-800 dark:text-slate-100"
                            :width="20"
                            :height="20"
                        />
                    </div>
                    <div class="flex flex-col">
                        <p class="text-sm font-medium leading-5 text-[#1E293B] dark:text-slate-100">
                            Missing required details:
                        </p>
                        <p class="text-sm font-normal leading-5 text-[#64748B] dark:text-slate-400">
                            Review and complete details before adding to a trip.
                        </p>
                    </div>
                </div>
            </div>

            <!-- Loading -->
            <div
                v-if="loadingTrips"
                class="flex items-center justify-center py-8"
            >
                <i
                    class="fas fa-spinner-third fa-spin text-slate-400 text-lg"
                ></i>
            </div>

            <template v-else>
                <p class="text-[#475569] dark:text-slate-400 text-sm font-medium leading-5 mb-2">
                    {{
                        mode === 'add'
                            ? 'Select trip to add redemption'
                            : 'Select trip to move redemption'
                    }}
                </p>

                <!-- Trip list -->
                <div>
                    <template
                        v-for="(trip, index) in filteredTrips"
                        :key="trip.id"
                    >
                        <div
                            class="flex items-center gap-4 py-4 cursor-pointer transition-all rounded-lg px-2 -mx-2 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-slate-300"
                            :class="
                                selectedTripId === trip.id
                                    ? 'bg-slate-50 dark:bg-slate-900/40'
                                    : 'hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900/40'
                            "
                            tabindex="0"
                            role="option"
                            :aria-selected="selectedTripId === trip.id"
                            @click="
                                selectedTripId = trip.id;
                                validationError = false;
                            "
                        >
                            <div
                                class="w-12 h-12 rounded-lg shrink-0 bg-slate-100 dark:bg-slate-700 bg-center bg-cover trip-thumbnail"
                                :style="
                                    getTripImageUrl(trip)
                                        ? {
                                              backgroundImage: `url(${getTripImageUrl(trip)})`,
                                          }
                                        : {}
                                "
                            ></div>
                            <div class="min-w-0 flex-1">
                                <p
                                    class="text-sm font-medium text-[#1E293B] dark:text-slate-100 leading-5 truncate"
                                >
                                    {{ trip.title }}
                                </p>
                                <p class="text-sm text-[#64748B] dark:text-slate-400 leading-5">
                                    {{ formatTripDate(trip.start_date) }}
                                    <span v-if="trip.end_date">
                                        – {{ formatTripDate(trip.end_date) }}
                                    </span>
                                </p>
                            </div>
                            <div
                                v-if="selectedTripId === trip.id"
                                class="ml-auto flex items-center justify-center w-8 h-8 rounded-full bg-10x-orange text-white shrink-0"
                            >
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 16 16"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M13.3334 4L6.00008 11.3333L2.66675 8"
                                        stroke="white"
                                        stroke-width="2.5"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                </svg>
                            </div>
                        </div>
                        <hr
                            v-if="index < filteredTrips.length - 1"
                            class="border-t border-[#E2E8F0] dark:border-slate-700"
                        />
                    </template>

                    <!-- New trip option -->
                    <hr
                        v-if="filteredTrips.length > 0"
                        class="border-t border-[#E2E8F0] dark:border-slate-700"
                    />
                    <div
                        class="flex items-center gap-4 py-4 cursor-pointer transition-all rounded-lg px-2 -mx-2"
                        :class="
                            selectedTripId === 'new'
                                ? 'bg-slate-50 dark:bg-slate-900/40'
                                : 'hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900/40'
                        "
                        tabindex="0"
                        role="option"
                        :aria-selected="selectedTripId === 'new'"
                        @click="
                            selectedTripId = 'new';
                            validationError = false;
                        "
                    >
                        <div
                            class="w-12 h-12 rounded-lg shrink-0 bg-slate-100 dark:bg-slate-700 flex items-center justify-center"
                        >
                            <Icon
                                icon="trip-insurance"
                                :width="20"
                                :height="20"
                                class="text-slate-500 dark:text-slate-400"
                                pathClass="fill-slate-500"
                            />
                        </div>
                        <div class="min-w-0 flex-1">
                            <p
                                class="text-sm font-medium text-[#1E293B] dark:text-slate-100 leading-5"
                            >
                                New trip
                            </p>
                            <p class="text-sm text-[#64748B] dark:text-slate-400 leading-5">
                                {{
                                    filteredTrips.length === 0
                                        ? 'You can specify trip details in next step'
                                        : 'Create a new trip and add redemption'
                                }}
                            </p>
                        </div>
                        <div
                            v-if="selectedTripId === 'new'"
                            class="ml-auto flex items-center justify-center w-8 h-8 rounded-full bg-[#FD421D] text-white shrink-0"
                        >
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M13.3334 4L6.00008 11.3333L2.66675 8"
                                    stroke="white"
                                    stroke-width="2.5"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                />
                            </svg>
                        </div>
                    </div>
                </div>

                <!-- Validation error -->
                <div
                    v-if="validationError"
                    class="flex items-center gap-1.5 py-2"
                >
                    <Icon
                        icon="caution"
                        :width="14"
                        :height="14"
                        class="text-red-500"
                    />
                    <span class="text-sm text-red-500">
                        {{
                            mode === 'add'
                                ? 'Select trip to add redemption'
                                : 'Select trip to move redemption'
                        }}
                    </span>
                </div>
            </template>
        </template>

        <template #footer>
            <div class="flex justify-end items-center gap-3">
                <button
                    type="button"
                    class="px-4 py-2 text-sm font-medium text-slate-800 dark:text-slate-100 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900/40"
                    @click="handleClose"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    class="px-4 py-2 text-sm font-medium text-white bg-slate-800 rounded shadow-sm hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    :disabled="submitting || hasMissingDetails"
                    @click="handleConfirm"
                >
                    {{
                        selectedTripId === 'new'
                            ? 'Create new trip'
                            : mode === 'add'
                              ? 'Save & add to trip'
                              : 'Move redemption'
                    }}
                    <span class="inline ml-2" v-if="submitting">
                        <i class="fas fa-spinner-third fa-spin"></i>
                    </span>
                </button>
            </div>
        </template>
    </UiModalMobile>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import UiModal from '@/components/ui/modal/ui-modal.vue';
import UiModalMobile from '@/components/ui/modal/ui-modal-mobile.vue';
import Icon from '@/components/ui/icon/icon.vue';
import { Apii } from '@/services/api';
import { useIsMobile } from '@/composables/useIsMobile';
import { notify } from 'notiwind';

const { isMobile } = useIsMobile(640);

const ApiService = new Apii();

const props = defineProps({
    isOpen: {
        type: Boolean,
        default: false,
    },
    redemption: {
        type: Object,
        default: null,
    },
    mode: {
        type: String,
        default: 'move',
        validator: (value) => ['move', 'add'].includes(value),
    },
});

const emit = defineEmits(['close', 'moved', 'new-trip']);

const loadingTrips = ref(false);
const availableTrips = ref([]);
const selectedTripId = ref(null);
const submitting = ref(false);
const validationError = ref(false);

const cdnUrl = computed(() => window.cdnUrl);

const subtitle = computed(() => {
    if (!props.redemption) return '';
    const parts = [];
    const name = props.redemption.name || props.redemption.title || '';
    if (name) parts.push(name);
    const date = props.redemption.date || props.redemption.start_date;
    if (date) parts.push(formatTripDate(date));
    return parts.join(', ');
});

const hasMissingDetails = computed(() => {
    return props.redemption?.import_status === 'needs_details';
});

const filteredTrips = computed(() => {
    if (!props.redemption?.trip_id) return availableTrips.value;
    return availableTrips.value.filter(
        (trip) => trip.id !== props.redemption.trip_id
    );
});

const getTripImageUrl = (trip) => {
    if (!trip.image?.path) return null;
    return `${cdnUrl.value}/${trip.image.path}/${trip.image.file_name}`;
};

const formatTripDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
};

const fetchTrips = async () => {
    loadingTrips.value = true;
    try {
        const response = await ApiService.apiCall('/trips', 'get');
        const data = response.data || response;
        const upcomingData =
            data.upcoming_trips?.data || data.upcoming_trips || [];
        const pastData = data.past_trips?.data || data.past_trips || [];
        availableTrips.value = [...upcomingData, ...pastData];
    } catch (err) {
        console.error('Failed to fetch trips:', err);
        availableTrips.value = [];
    } finally {
        loadingTrips.value = false;
    }
};

const handleClose = () => {
    emit('close');
};

const handleConfirm = async () => {
    if (!props.redemption) return;

    if (!selectedTripId.value) {
        validationError.value = true;
        return;
    }

    if (selectedTripId.value === 'new') {
        emit('new-trip', props.redemption);
        return;
    }

    const trip = availableTrips.value.find(
        (t) => t.id === selectedTripId.value
    );

    submitting.value = true;
    try {
        const endpoint =
            props.mode === 'add'
                ? `/redemptions/${props.redemption.id}/attach-to-trip`
                : `/redemptions/${props.redemption.id}/move-to-trip`;

        await ApiService.apiCall(endpoint, 'post', {
            trip_id: selectedTripId.value,
        });
        notify(
            {
                group: 'success',
                title:
                    props.mode === 'add'
                        ? 'Redemption added'
                        : 'Redemption moved',
                text:
                    props.mode === 'add'
                        ? `Redemption successfully added to ${trip?.title || 'trip'}`
                        : `Redemption successfully moved to ${trip?.title || 'trip'}`,
            },
            5000
        );
        emit('moved', trip);
    } catch (err) {
        console.error(`Failed to ${props.mode} redemption:`, err);
    } finally {
        submitting.value = false;
    }
};

watch(
    () => props.isOpen,
    (newVal) => {
        if (newVal) {
            selectedTripId.value = null;
            validationError.value = false;
            fetchTrips();
        }
    }
);
</script>

<style scoped>
.trip-thumbnail {
    background-image: url('@@/images/default-placeholder.png');
    background-size: cover;
    background-position: center;
}
</style>
